import Pusher, * as PusherTypes from 'pusher-js';

import AuthenticationStore from '~/auth/authenticationStore';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import Configuration from '~/services/configuration';
import { logger } from '~/utils/logger';
import { captureException, captureMessage } from '~/utils/sentry';

const authStore = AuthenticationStore();

export type SubscribeCallbacks = {
  success: (data: PusherTypes.Members) => void;
  error?: (error: SubscriptionError) => void;
};

export type ChannelsTable = {
  [index: string]: PusherTypes.Channel | PusherTypes.PresenceChannel;
};

interface SubscriptionError {
  type: string;
  error: string;
  status: number;
}

interface PusherData {
  code: number;
  message: string;
}

interface PusherError {
  error?: { type: string; data: PusherData };
  data?: PusherData;
  type: string;
}

const channelJoinTypes = new Map<string, string>();

let pusherInstance: PusherTypes.default | undefined;

let hasConnectionError = false;

let hasEverConnected = false;

/** A set of callbacks plus the register-returns-unregister shape both uses want. */
function createListenerRegistry() {
  const listeners = new Set<() => void>();
  return {
    clear: () => listeners.clear(),
    emit: () => listeners.forEach((listener) => listener()),
    register: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const connectionListeners = createListenerRegistry();

const resyncListeners = createListenerRegistry();

/**
 * Pusher never replays events published while the socket was down, so anything
 * holding a subscription has to re-establish truth itself once the connection
 * comes back. Callbacks registered here run on every reconnect (never on the
 * initial connect, where nothing can have been missed).
 */
export const registerPusherResync = resyncListeners.register;

function setHasConnectionError(value: boolean) {
  if (value === hasConnectionError) return;
  hasConnectionError = value;
  connectionListeners.emit();
}

export const subscribePusherConnection = connectionListeners.register;

export function getPusherConnectionSnapshot() {
  return hasConnectionError;
}

const createPusherInstance = () => {
  const pusherAuthEndpoint = authStore.isRolodexAuthed()
    ? `${Configuration.rolodexEndpoint}/api/v1/pusher/auth`
    : `${Configuration.authEndpoint}/v1/auth/pusher-auth`;

  // Tokens are read per attempt rather than captured, so a refresh that landed
  // between the first attempt and the retry is picked up.
  const requestChannelAuth = (
    socketId: string,
    channelName: string,
    joinType: string | undefined,
  ) => {
    const body = new URLSearchParams({
      socket_id: socketId,
      channel_name: channelName,
    });
    if (joinType) {
      body.append('join_type', joinType);
    }
    return fetch(pusherAuthEndpoint, {
      method: 'post',
      body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        ...(authStore.getCsrf()
          ? {
              [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: authStore.getCsrf(),
            }
          : {}),
        ...(authStore.getRefreshToken()
          ? {
              Authorization: `Bearer ${authStore.getRefreshToken()}`,
            }
          : {}),
      },
    });
  };

  return new Pusher(getEnvVariable('PUSHER_APP_KEY'), {
    cluster: getEnvVariable('PUSHER_CLUSTER'),
    channelAuthorization: {
      endpoint: pusherAuthEndpoint,
      transport: 'ajax',
      customHandler: async (payload, callback) => {
        const { socketId, channelName } = payload;
        const joinType = channelJoinTypes.get(channelName);
        try {
          let response = await requestChannelAuth(
            socketId,
            channelName,
            joinType,
          );
          if (response.status === 401) {
            response = await requestChannelAuth(
              socketId,
              channelName,
              joinType,
            );
          }
          // `fetch` resolves on 4xx/5xx, so without this an error body would be
          // handed to Pusher as a valid auth payload and the subscription would
          // fail later as an unattributable `pusher:subscription_error`.
          if (!response.ok) {
            throw new Error(
              `Pusher channel authorization failed with status ${response.status}`,
            );
          }
          const token = await response.json();
          callback(null, token);
        } catch (error: unknown) {
          const err = error instanceof Error ? error : new Error(String(error));
          logger.pusher('channel auth failed for %s: %s', channelName, err);
          captureException(
            `Pusher channel authorization failed for ${channelName}`,
            err,
            { section: 'pusher auth' },
          );
          callback(err, null);
        } finally {
          channelJoinTypes.delete(channelName);
        }
      },
    },
  });
};

function bindToPusherErrorEvent() {
  if (!pusherInstance) return;

  pusherInstance.connection.bind('error', (err: PusherError) => {
    const errorCode = err?.error?.data?.code;
    const applicationDoesNotExist = errorCode === 4001;
    const applicationDisabled = errorCode === 4003;
    const applicationIsOverQuota = errorCode === 4004;
    if (
      applicationIsOverQuota ||
      applicationDoesNotExist ||
      applicationDisabled
    ) {
      setHasConnectionError(true);
      captureException(
        err?.error?.data?.message ??
          'Unexpected pusher connection error, check Pusher dashboard for possible application issues.',
        err,
        {
          section: 'pusher error',
        },
      );
    }
  });
}

function bindToPusherConnectionState() {
  if (!pusherInstance) return;

  pusherInstance.connection.bind(
    'state_change',
    ({ previous, current }: { previous: string; current: string }) => {
      logger.pusher('connection %s → %s', previous, current);

      if (current === 'connected') {
        setHasConnectionError(false);
        // The initial connect cannot have missed anything; only a reconnect
        // leaves a gap that subscribers need to close.
        if (hasEverConnected) {
          resyncListeners.emit();
        }
        hasEverConnected = true;
        return;
      }

      if (current === 'unavailable' || current === 'failed') {
        captureMessage(`Pusher connection ${current}`, {
          section: 'pusher connection',
          previous_state: previous,
        });
      }
    },
  );
}

function getPusher() {
  if (!pusherInstance) {
    pusherInstance = createPusherInstance();
    bindToPusherErrorEvent();
    bindToPusherConnectionState();
  }
  return pusherInstance;
}

function subscribe(
  pusher: PusherTypes.default,
  channelName: string,
  { success, error }: SubscribeCallbacks,
  joinType?: string,
) {
  if (joinType) {
    channelJoinTypes.set(channelName, joinType);
  }
  const channel = pusher.subscribe(channelName);

  // `pusher.subscribe` returns the cached channel object when one already
  // exists, and `pusher.unsubscribe` only drops it from that cache on the
  // settled path — while a subscription is still pending it calls
  // `cancelSubscription()` and leaves the object, and its bindings, in place. So
  // a resubscribe after a fast unsubscribe would stack a second pair of
  // lifecycle handlers and report every success/failure twice. Clearing first
  // makes this idempotent; these two events are bound nowhere else.
  channel.unbind('pusher:subscription_succeeded');
  channel.unbind('pusher:subscription_error');

  channel.bind('pusher:subscription_succeeded', (data: PusherTypes.Members) => {
    success?.(data);
  });
  channel.bind('pusher:subscription_error', (errorData: SubscriptionError) => {
    error?.(errorData);
  });

  return channel;
}

function getChannels(pusher: PusherTypes.default) {
  return pusher.channels.channels;
}

function getPresenceChannelMembers(
  pusher: PusherTypes.default,
  channelName: string,
  shouldUnsubscribeAfter = false,
  joinType?: string,
): Promise<PusherTypes.Members> {
  return new Promise((resolve, reject) => {
    subscribe(
      pusher,
      channelName,
      {
        success: (data) => {
          shouldUnsubscribeAfter && pusher.unsubscribe(channelName);
          resolve(data);
        },
        error: (err) => reject(err),
      },
      joinType,
    );
  });
}

export const pusherStore = {
  getPusher,
  subscribe,
  getChannels,
  getPresenceChannelMembers,
};

export function resetPusherStoreForTests() {
  pusherInstance = undefined;
  hasConnectionError = false;
  hasEverConnected = false;
  channelJoinTypes.clear();
  connectionListeners.clear();
  resyncListeners.clear();
}
